import { apiService } from '../services/apiService';

/**
 * PDF生成工具函數集合
 * 使用示例和常用場景
 */

// 生成訂單概覽PDF
export const generateInboundVoucherPDF = async (orderNumber: string) => {
  const { PDFService } = await import('../services/pdfService');
  await PDFService.generatePDFFromAPI(
    () => apiService.getInboundVoucherHTML(orderNumber),
    {
      filename: `訂單概覽-${orderNumber}.pdf`,
      loadingMessage: '正在生成訂單概覽PDF...',
      successMessage: '訂單概覽PDF已在新窗口中打開',
      errorMessage: '訂單概覽PDF生成失敗，請稍後重試',
      quality: 2
    }
  );
};

// 獲取入庫憑證HTML
export const generateInboundBeePDF = async (orderNumber: string) => {
    const { PDFService } = await import('../services/pdfService');
    await PDFService.generatePDFFromAPI(
        () => apiService.getInboundBeePDF(orderNumber),
        {
            filename: `入庫憑證-${orderNumber}.pdf`,
            loadingMessage: '正在生成入庫憑證PDF...',
            successMessage: '入庫憑證PDF已在新窗口中打開',
            errorMessage: '入庫憑證PDF生成失敗，請稍後重試',
            quality: 2
        }
    );
};


//  生成揀貨單PDF
export const generatePickingOrderPDF = async (batchNumber: string) => {
  const { PDFService } = await import('../services/pdfService');
  await PDFService.generatePDFFromAPI(
    () => apiService.getPickingOrderHTML(batchNumber),
    {
      filename: `picking-order-${batchNumber}.pdf`,
      loadingMessage: '正在生成揀貨單PDF...',
      successMessage: '揀貨單PDF已在新窗口中打開',
      errorMessage: '揀貨單PDF生成失敗，請稍後重試',
      quality: 1,
      paperSize: 'a4',
      orientation: 'portrait'
    }
  );
};

// 生成運單PDF【HKtvmall】
export const getHktvMallWaybillHtml = async (orderNumber: string, waybillNumber: string) => {
    const { PDFService } = await import('../services/pdfService');
    await PDFService.generatePDFFromAPI(
        () => apiService.getHktvMallWaybillHtml(orderNumber, waybillNumber), // 假設有這個API
        {
            filename: `waybill-${waybillNumber}.pdf`,
            loadingMessage: '正在生成運單PDF...',
            successMessage: '運單PDF已在新窗口中打開',
            errorMessage: '運單PDF生成失敗，請稍後重試',
            quality: 2,
            paperSize: 'a5',
            orientation: 'landscape'
        }
    );
};

// 獲取運單HTML【不是HKtvmall】
export const getWaybillHtml = async (orderNumber: string, waybillNumber: string) => {
    const { PDFService } = await import('../services/pdfService');
    await PDFService.generatePDFFromAPI(
        () => apiService.getWaybillHtml(orderNumber, waybillNumber), // 假設有這個API
        {
            filename: `waybill-${waybillNumber}.pdf`,
            loadingMessage: '正在生成運單PDF...',
            successMessage: '運單PDF已在新窗口中打開',
            errorMessage: '運單PDF生成失敗，請稍後重試',
            quality: 2,
            paperSize: 'a5',
            orientation: 'landscape'
        }
    );
};

// 【新增】自動打印 HKTVmall 運單（同時生成PDF）
export const printHktvMallWaybillAuto = async (orderNumber: string, waybillNumber: string) => {
    const { PDFService } = await import('../services/pdfService');
    await PDFService.printAndGeneratePDFFromAPI(
        () => apiService.getHktvMallWaybillHtml(orderNumber, waybillNumber),
        {
            filename: `waybill-${waybillNumber}.pdf`,
            printTitle: `運單 ${waybillNumber}`,
            loadingMessage: '正在獲取運單資料...',
            successMessage: '打印對話框已打開，PDF已開始下載',
            errorMessage: '運單打印失敗，請稍後重試',
            quality: 2,
            paperSize: 'a5',
            orientation: 'landscape',
            onPrintComplete: () => {
                console.log(`運單 ${waybillNumber} 打印完成`);
            }
        }
    );
};

// 【新增】自動打印普通運單（同時生成PDF）
export const printWaybillAuto = async (orderNumber: string, waybillNumber: string) => {
    const { PDFService } = await import('../services/pdfService');
    await PDFService.printAndGeneratePDFFromAPI(
        () => apiService.getWaybillHtml(orderNumber, waybillNumber),
        {
            filename: `waybill-${waybillNumber}.pdf`,
            printTitle: `運單 ${waybillNumber}`,
            loadingMessage: '正在獲取運單資料...',
            successMessage: '打印對話框已打開，PDF已開始下載',
            errorMessage: '運單打印失敗，請稍後重試',
            quality: 2,
            paperSize: 'a5',
            orientation: 'landscape',
            onPrintComplete: () => {
                console.log(`運單 ${waybillNumber} 打印完成`);
            }
        }
    );
};

// 【新增】靜默打印 HKTVmall 運單（不打開新視窗）
export const printHktvMallWaybillSilently = async (orderNumber: string, waybillNumber: string) => {
    const { PDFService } = await import('../services/pdfService');
    await PDFService.printPDFSilentlyFromAPI(
        () => apiService.getHktvMallWaybillHtml(orderNumber, waybillNumber),
        {
            filename: `waybill-${waybillNumber}.pdf`,
            printTitle: `運單 ${waybillNumber}`,
            loadingMessage: '正在生成運單...',
            successMessage: `運單 ${waybillNumber} 已發送至打印機`,
            errorMessage: '運單打印失敗，請稍後重試',
            quality: 2, // Balanced quality for faster generation
            paperSize: 'a5',
            orientation: 'landscape',
            onPrintComplete: () => {
                console.log(`運單 ${waybillNumber} 靜默打印完成`);
            }
        }
    );
};

// 【新增】靜默打印普通運單（不打開新視窗）
export const printWaybillSilently = async (orderNumber: string, waybillNumber: string) => {
    const { PDFService } = await import('../services/pdfService');
    await PDFService.printPDFSilentlyFromAPI(
        () => apiService.getWaybillHtml(orderNumber, waybillNumber),
        {
            filename: `waybill-${waybillNumber}.pdf`,
            printTitle: `運單 ${waybillNumber}`,
            loadingMessage: '正在生成運單...',
            successMessage: `運單 ${waybillNumber} 已發送至打印機`,
            errorMessage: '運單打印失敗，請稍後重試',
            quality: 2, // Balanced quality for faster generation
            paperSize: 'a5',
            orientation: 'landscape',
            onPrintComplete: () => {
                console.log(`運單 ${waybillNumber} 靜默打印完成`);
            }
        }
    );
};

// 從HTML字符串直接生成PDF
export const generatePDFFromString = async (htmlContent: string, filename: string) => {
  const { PDFService } = await import('../services/pdfService');
  await PDFService.generatePDFFromHTMLString(htmlContent, {
    filename,
    loadingMessage: '正在生成PDF...',
    successMessage: 'PDF已在新窗口中打開',
    errorMessage: 'PDF生成失敗，請稍後重試',
    quality: 2
  });
};

/*// 示例5: 批量生成PDF（依次生成）
export const generateMultiplePDFs = async (orders: Array<{orderNumber: string, type: 'inbound' | 'picking' | 'waybill'}>) => {
  for (const order of orders) {
    try {
      switch (order.type) {
        case 'inbound':
          await generateInboundVoucherPDF(order.orderNumber);
          break;
        case 'picking':
          await generatePickingOrderPDF(order.orderNumber);
          break;
        case 'waybill':
          await generateWaybillPDF(order.orderNumber);
          break;
      }
      // 添加延遲避免瀏覽器阻止過多彈窗
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error(`生成${order.type} PDF失敗:`, order.orderNumber, error);
    }
  }
};

// 示例6: 自定義配置的PDF生成
export const generateCustomPDF = async (
  apiCall: () => Promise<string>,
  options: {
    filename: string;
    paperSize?: 'a4' | 'a3' | 'letter';
    orientation?: 'portrait' | 'landscape';
    quality?: number;
    downloadDirectly?: boolean;
  }
) => {
  const { PDFService } = await import('../services/pdfService');
  await PDFService.generatePDFFromAPI(apiCall, {
    ...options,
    loadingMessage: '正在生成PDF...',
    successMessage: options.downloadDirectly ? 'PDF下載已開始' : 'PDF已在新窗口中打開',
    errorMessage: 'PDF生成失敗，請稍後重試'
  });
};*/
